import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ServiceArea {
  id: string;
  name: string;
  business_id: string;
  postal_codes: string[];
}

interface ServiceAreasModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  day: string;
  onSave: (selectedAreas: string[]) => void;
  currentAreas?: string[];
}

export default function ServiceAreasModal({
  isOpen,
  onClose,
  businessId,
  day,
  onSave,
  currentAreas = []
}: ServiceAreasModalProps) {
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<string[]>(currentAreas);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServiceAreas();
  }, [businessId]);

  const fetchServiceAreas = async () => {
    if (!supabase) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', businessId)
        .eq('is_active', true);

      if (error) throw error;
      console.log('Fetched service areas:', data);
      setServiceAreas(data || []);
    } catch (err) {
      console.error('Error fetching service areas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArea = (areaId: string) => {
    console.log('Toggling area:', areaId);
    setSelectedAreas(prev => 
      prev.includes(areaId)
        ? prev.filter(id => id !== areaId)
        : [...prev, areaId]
    );
  };

  const handleSave = () => {
    console.log('Saving selected areas:', selectedAreas);
    onSave(selectedAreas);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed inset-x-4 top-[50%] translate-y-[-50%] sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:max-w-lg w-full bg-white rounded-lg shadow-lg z-50">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Select Service Areas for {day}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="text-center py-4">Loading service areas...</div>
          ) : serviceAreas.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No service areas found
            </div>
          ) : (
            <div className="space-y-2">
              {serviceAreas.map((area) => (
                <label
                  key={area.id}
                  className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-md cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedAreas.includes(area.id)}
                    onChange={() => handleToggleArea(area.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm text-gray-900">{area.name}</span>
                    <div className="mt-1 text-xs text-gray-500">
                      {area.postal_codes.join(', ')}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 px-4 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
          >
            Save
          </button>
        </div>
      </div>
    </>
  );
} 